import { storedLanguage } from "../i18n";

/*
 * Which language the Google Maps script was loaded in, and whether that is still the right one.
 *
 * A module of its own, rather than an export from `MapView`, purely to keep the import graph
 * acyclic: `MapView` already imports `LocaleContext` for its `t`, so `LocaleContext` importing
 * `MapView` back would close a cycle. Both sides import this instead, and neither imports the
 * other.
 *
 * ── Why any of this exists ───────────────────────────────────────────────────────────────────
 *
 * The Maps API is a global singleton, configured once before the first `importLibrary` call and
 * not reconfigurable afterwards. Its script is in the DOM with `language=el` baked in, and
 * everything it draws is fixed at that: the labels on the tiles, the POI names, and Google's own
 * chrome ("View larger map", "Terms"). Unmounting the React component and mounting a new one does
 * not help — a new `Map` instance still runs on the same loaded API.
 *
 * Tearing the API down and re-injecting it (deleting `window.google`, removing the script tags) is
 * an unsupported path: the API registers global callbacks and listeners, the loader memoises
 * internally, and the usual result is "You have included the Google Maps JavaScript API multiple
 * times" plus a map that half works. So the only reliable way to re-language a map is a document
 * reload.
 *
 * What this module buys is *when* that reload happens. Reloading the instant the language changes
 * interrupts whatever the user was doing — usually reading Profile, where there is no map on
 * screen at all. Instead the switch is allowed to complete normally, the mismatch is recorded, and
 * the reload is deferred to the moment a map is actually about to be drawn. That lands it on a
 * route change, where a page load is indistinguishable from ordinary navigation.
 *
 * Note this covers Google's own rendering only. Place names the app resolves itself — see
 * hooks/usePlaceName — are keyed by language and re-fetch on a switch with no reload at all.
 */

/** The language Maps actually loaded in, or null if it has never been loaded this page-load. */
let loaded = null;

/** How many MapView instances are currently mounted. */
let mounted = 0;

/** Called by MapView at the moment it configures the loader. */
export const noteMapsLanguage = (lang) => { loaded = lang; };

/** Whether a loaded map is now showing a language the user is no longer reading in. */
export const mapsLanguageIsStale = () => loaded !== null && loaded !== storedLanguage();

/** Whether a map is on screen right now, which decides reload-now vs reload-on-next-map. */
export const someMapIsMounted = () => mounted > 0;

/** Bookkeeping for the above. Returns the unregister fn, so callers can use it as effect cleanup. */
export const registerMap = () => {
  mounted += 1;
  let released = false;
  return () => {
    // Guarded because React 18 StrictMode runs effect cleanups twice in development, and a
    // double decrement would report "no maps mounted" while one is still on screen.
    if (released) return;
    released = true;
    mounted = Math.max(0, mounted - 1);
  };
};
